import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaretRight, Check, CheckCircle } from '@phosphor-icons/react';
import ButtonSpinner from '../ButtonSpinner';
import { BTN_PRIMARY_SM } from '../buttonStyles';
import { busyStyle } from '../../utils/busyStyle';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/useToast';
import { useUserIdentity } from '../../hooks/useUserIdentity';
import { useViewerZone } from '../../hooks/useViewerZone';
import { acceptLiveTimeCard, subscribeTimeCard, type LessonMode, type LiveTimeCard } from '../../services/liveV2';
import { liveLessonsErrorMessage } from '../../services/liveLessons';
import { whenLine } from '../../utils/liveCalendar';
import { Skeleton } from '../Skeleton';
import { firstNameOf } from '../../utils/liveLessonText';

/**
 * A Suggest a Time or Request a Time card in a thread (redesign spec, batch
 * 4): who sent it, "Fri, Oct 23 at 3:00 PM · 45 min", and Accept for the
 * side it was sent to, which books it (acceptLiveTimeCard). A card sent
 * without a mode asks for one at Accept. Booked once accepted; the sender
 * sees it waiting.
 */
export function TimeCardBubble({ cardId, startAt, durationMinutes = 45 }: {
  cardId: string;
  startAt: string;
  durationMinutes?: number;
}) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { showError } = useToast();
  const tz = useViewerZone();
  const [card, setCard] = useState<LiveTimeCard | null | undefined>(undefined);
  const [mode, setMode] = useState<LessonMode | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => subscribeTimeCard(cardId, setCard), [cardId]);
  const coach = useUserIdentity(card?.coachId, 'coach');
  const player = useUserIdentity(card?.playerId, 'player');
  // Loading and unreadable still show the time the message carries, never an empty gap in the thread (S12).
  if (!card) {
    return (
      <div className="my-3 flex justify-center" data-time-card aria-busy={card === undefined}>
        <div className="w-full max-w-[320px] rounded-[12px] border border-[#e6e6e6] bg-white px-4 py-3.5">
          {card === undefined
            ? <Skeleton width={140} height={13} radius={4} />
            : <div className="text-[13px] leading-[18px] text-db-text-2">Could not load this time.</div>}
          <div className="mt-0.5 text-[17px] leading-[22px] font-semibold tracking-[-0.43px] text-db-text-1">
            {whenLine(startAt, tz)} · {durationMinutes} min
          </div>
        </div>
      </div>
    );
  }

  const sentByMe = (card.sentBy === 'coach' ? card.coachId : card.playerId) === currentUser?.uid;
  const coachFirst = firstNameOf(coach?.name, 'Your coach');
  const playerFirst = firstNameOf(player?.name, 'Your player');
  const headline = card.kind === 'suggest'
    ? (sentByMe ? 'You suggested a time' : `${coachFirst} suggested a time`)
    : (sentByMe ? 'You requested a time' : `${playerFirst} requested a time`);
  const needsMode = !card.lessonMode;
  const chosen = card.lessonMode ?? mode;

  const accept = async () => {
    setBusy(true);
    try {
      await acceptLiveTimeCard({ cardId, ...(needsMode && chosen && { lessonMode: chosen }) });
    } catch (err) {
      showError(liveLessonsErrorMessage(err, 'Could not book this time. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const chip = (m: LessonMode, label: string) => (
    <button key={m} type="button" aria-pressed={mode === m} onClick={() => setMode(m)}
      className={
        // A choice, not an action: chosen is the tint wash with a check, so Accept stays the one fill (audit S4).
        "ios-press relative inline-flex h-9 cursor-pointer items-center gap-1 rounded-[100px] px-3 text-[15px] leading-[20px] font-medium " +
        "after:absolute after:inset-x-0 after:-inset-y-1 after:content-[''] " +
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40 ' +
        (mode === m ? 'bg-[rgba(26,107,138,0.12)] text-biscayne' : 'bg-[rgba(120,120,128,0.16)] text-black')
      }>
      {mode === m && <Check size={14} weight="bold" aria-hidden="true" />}{label}
    </button>
  );

  return (
    <div className="my-3 flex justify-center" data-time-card>
      <div className="w-full max-w-[320px] rounded-[12px] border border-[#e6e6e6] bg-white px-4 py-3.5">
        <div className="text-[13px] leading-[18px] text-db-text-2">{headline}</div>
        <div className="mt-0.5 text-[17px] leading-[22px] font-semibold tracking-[-0.43px] text-db-text-1">
          {whenLine(startAt, tz)} · {durationMinutes} min
        </div>
        {card.lessonMode && (
          <div className="text-[13px] leading-[18px] text-db-text-2">
            {card.lessonMode === 'match_review' ? 'Match Review' : 'Guidance Call'}
          </div>
        )}
        {card.status === 'accepted' ? (
          card.bookingId ? (
            // The booked card opens the lesson's page.
            <button type="button" onClick={() => navigate(`/live/${card.bookingId}`)}
              className="ios-press relative mt-3 flex w-full cursor-pointer items-center gap-1.5 text-left text-[15px] leading-[20px] font-medium text-db-text-1 after:absolute after:-inset-y-3 after:inset-x-0 after:content-[''] focus:outline-none focus-visible:underline">
              <CheckCircle size={18} weight="fill" className="text-[#34C759]" aria-hidden="true" />
              <span className="flex-1">Booked</span>
              <CaretRight size={14} weight="bold" className="text-[rgba(60,60,67,0.3)]" aria-hidden="true" />
            </button>
          ) : (
            <div className="mt-3 flex items-center gap-1.5 text-[15px] leading-[20px] font-medium text-db-text-1">
              <CheckCircle size={18} weight="fill" className="text-[#34C759]" aria-hidden="true" /> Booked
            </div>
          )
        ) : sentByMe ? (
          <div className="mt-3 text-[13px] leading-[18px] text-db-text-2">
            Waiting for {card.kind === 'suggest' ? playerFirst : coachFirst}
          </div>
        ) : (
          <>
            {needsMode && (
              <div className="mt-3 flex flex-wrap gap-2">
                {chip('match_review', 'Match Review')}
                {chip('guidance_call', 'Guidance Call')}
              </div>
            )}
            <button type="button" onClick={() => { void accept(); }} disabled={busy || !chosen || card.status !== 'open'}
              aria-busy={busy} style={busyStyle(busy)} className={`${BTN_PRIMARY_SM} mt-3`}>
              {busy ? <ButtonSpinner size={16} /> : 'Accept'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
